/* eslint-disable max-len */
/* global DEBUG, ga */

/**
 * Send event to Google Analytics in production like environments, or log
 * to console when running locally.
 *
 * @param {string} eventCategory e.g. button
 * @param {string} eventAction e.g. click
 * @param {string} eventLabel e.g. start_button
 * @param {number} eventValue e.g. 55
 * @returns {void}
 */
export function logEvent(eventCategory, eventAction, eventLabel, eventValue) {
  if (!eventCategory || typeof eventCategory !== "string") {
    throw new Error("You must pass an event category string");
  }

  if (!eventAction || typeof eventAction !== "string") {
    throw new Error("You must pass an event action string");
  }

  if (!eventLabel || typeof eventLabel !== "string") {
    throw new Error("You must pass an event label string");
  }

  if (typeof eventValue !== "undefined" && typeof eventValue !== "number") {
    throw new Error("Event value must be a number");
  }

  const fieldsObject = {
    eventCategory,
    eventAction,
    eventLabel,
    eventValue
  };

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log(
      `ga("send", "event", ${Object.values(fieldsObject)
        .filter((v) => typeof v !== "undefined")
        .map((v) => (typeof v === "string" ? `"${v}"` : v))
        .join(", ")})`
    );
    return;
  }

  // eslint-disable-next-line consistent-return
  return ga("send", "event", fieldsObject);
}

/**
 * Log a button click
 *
 * @param {string} eventLabel Button type, e.g. "start_button"
 * @param {number} eventValue A value to associate to the button
 * @returns {void}
 */
export function logButtonClick(eventLabel, eventValue) {
  return logEvent("button", "click", eventLabel, eventValue);
}

export const NLURequestType = {
  audio: 0,
  event: 1,
  text: 2
};

/**
 * Log a successful NLU request
 *
 * @param {string} eventLabel Matched intent
 * @param {number} eventValue NLURequestType
 * @returns {void}
 */
export function logNLUSuccess(eventLabel, eventValue) {
  return logEvent("nlu_agent", "success", eventLabel, eventValue);
}

/**
 * Log a failed NLU request
 *
 * @param {string} eventLabel Request payload
 * @param {number} eventValue NLURequestType
 * @returns {void}
 */
export function logNLUFailure(eventLabel, eventValue) {
  return logEvent("nlu_agent", "failure", eventLabel, eventValue);
}

/**
 * Log video start
 *
 * @param {string} eventLabel Video title
 * @param {number} eventValue Video duration
 * @returns {void}
 */
export function logVideoStart(eventLabel, eventValue) {
  return logEvent("video", "start", eventLabel, eventValue);
}

/**
 * Log video completion
 *
 * @param {string} eventLabel Video title
 * @param {number} eventValue Video duration
 * @returns {void}
 */
export function logVideoComplete(eventLabel, eventValue) {
  return logEvent("video", "complete", eventLabel, eventValue);
}

/**
 * Log a successful getUserMedia request
 *
 * @returns {void}
 */
export function logGetUserMediaRequestSuccess() {
  return logEvent("getusermedia", "request", "successful");
}

/**
 * Log a failed getUserMedia request
 *
 * @returns {void}
 */
export function logGetUserMediaRequestFailure() {
  return logEvent("getusermedia", "request", "unsuccessful");
}

/**
 * Send user timing to Google Analytics in production like environments, or log
 * to console when running locally.
 *
 * @param {string} timingCategory e.g. getUserMedia
 * @param {string} timingVar e.g. latency
 * @param {number} timingValue milliseconds, e.g. 106
 * @param {string} timingLabel e.g. Time to request microphone
 * @returns {void}
 */
export function logTiming(timingCategory, timingVar, timingValue, timingLabel) {
  if (!timingCategory || typeof timingCategory !== "string") {
    throw new Error("You must pass a timing category string");
  }

  if (!timingVar || typeof timingVar !== "string") {
    throw new Error("You must pass a timing variable string");
  }

  if (typeof timingValue !== "number") {
    throw new Error("You must pass a timing value in milliseconds");
  }

  if (typeof timingLabel !== "undefined" && typeof timingLabel !== "string") {
    throw new Error("Timing label must be a string");
  }

  const fieldsObject = {
    timingCategory,
    timingVar,
    timingValue,
    timingLabel
  };

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log(
      `ga("send", "timing", ${Object.values(fieldsObject)
        .filter((v) => typeof v !== "undefined")
        .map((v) => (typeof v === "string" ? `"${v}"` : v))
        .join(", ")})`
    );
    return;
  }

  // eslint-disable-next-line consistent-return
  return ga("send", "timing", fieldsObject);
}
